import React, {VoidFunctionComponent, useState, useEffect, useRef} from 'react';
import {useHistory, useRouteMatch} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {useGoogleMap} from '@ubilabs/google-maps-react-hooks';
import uswSWR from 'swr';

import MapMarkerClusterer from './map-marker-clusterer/map-marker-clusterer';

import {useFilters} from '../../../hooks/useFilters';
import useInfoWindow from '../../../hooks/infowindow';

import {checkIsBeforeYear1945} from '../../libs/check-before-year-1945';
import createInfoWindowContent from '../../libs/create-info-window-content';
import {getMarkerIcon} from '../../libs/markers/get-marker-icons';
import {highlightMarker} from '../../libs/markers/highlight-marker';
import getLocalizedText from '../../libs/get-localized-text';

import {Work, WorkId} from '../../../../../shared/types/work';
import {MarkersData, MarkerData} from '../../../../../shared/types/marker-data';
import {YearFilterValues} from '../../../../../shared/types/filter';

/**
 * Component to render all map markers
 */
const MapMarkers: VoidFunctionComponent<Record<string, unknown>> = () => {
  const {locale} = useIntl();
  const {map} = useGoogleMap();
  const {
    currentCategoryFilterValues,
    currentYearFilterValues,
    currentTagFilterValues
  } = useFilters();
  const history = useHistory();
  const match = useRouteMatch<{workId: WorkId}>('/works/:workId');
  const workId = match?.params.workId;

  const [markers, setMarkers] = useState<Array<google.maps.Marker>>([]);
  const handleMarkerClick = useRef<(id: WorkId) => void>(() => {});

  const infoWindow = useInfoWindow();
  const handleMarkerMouseOver = useRef<(marker: google.maps.Marker) => void>(
    () => {}
  );

  const {data: workData} = uswSWR('works');

  // Function to navigate back to the home screen
  const returnToHomeScreen = () => {
    history.push('/');
  };

  // Handle route changes
  useEffect(() => {
    // Update marker click handler
    handleMarkerClick.current = (markerWorkId: WorkId) => {
      if (markerWorkId === workId) {
        // Go back to home if same marker was clicked again
        returnToHomeScreen();
      } else {
        // Go to work's page
        history.push(`/works/${markerWorkId}`);
      }
    };

    // Handle marker selection
    markers.forEach((marker: google.maps.Marker) => {
      const markerId = marker.get('id');
      const markerYear = marker.get('year');
      const isSelected = markerId === workId;

      // Update marker icon
      marker.setIcon(getMarkerIcon(markerYear, isSelected));

      if (!map) {
        return;
      }

      // Highlight clicked marker
      if (isSelected) {
        highlightMarker(map, marker);
      }
    });

    // Navigate to home screen and deselect marker when map is clicked
    if (workId) {
      map?.addListener('click', returnToHomeScreen);
    }

    // Remove event listener
    return () => {
      if (map) {
        google.maps.event.clearListeners(map, 'click');
      }
    };
  }, [workId]);

  // Handle infowindow visibility for each marker
  useEffect(() => {
    // Update marker mouseover handler
    handleMarkerMouseOver.current = (marker: google.maps.Marker) => {
      if (!infoWindow) {
        return;
      }

      const markerTitle = marker.get('title');
      const markerArtist = marker.get('artist');
      const markerYear = marker.get('year');
      const markerPosition = marker.getPosition();

      // Create info window content for each marker
      const content = createInfoWindowContent(
        markerTitle,
        markerYear,
        markerArtist
      );

      if (!markerPosition) {
        return;
      }

      // Open info window on top of marker
      infoWindow.setPosition(markerPosition);
      infoWindow.setContent(content);
      infoWindow.open(map);
    };
  }, [markers, infoWindow, locale]);

  // Define marker data from given art works data and create google map markers
  useEffect(() => {
    if (map && workData) {
      const markerData = workData.map(
        (work: Work): MarkerData => ({
          id: work.id,
          latitude: work.latitude,
          longitude: work.longitude,
          title: work.title,
          year: work.year,
          category: work.category,
          artist: work.artist,
          tags: work.tags
        })
      ) as MarkersData;

      const newMarkers: Array<google.maps.Marker> = [];

      // Add markers when data was loaded and formatted
      markerData.forEach((markerEntry: MarkerData) => {
        const {latitude, longitude, title, id, year, category, artist, tags} =
          markerEntry;
        const icon = getMarkerIcon(year, false);

        if (!icon) {
          return;
        }

        const markerOptions: google.maps.MarkerOptions = {
          map,
          position: {lat: latitude, lng: longitude},
          optimized: false,
          clickable: true,
          title,
          icon
        };

        // Create new markers with click listeners
        const marker = new google.maps.Marker(markerOptions);
        marker.set('id', id);
        marker.set('year', year);
        marker.set('category', category);
        marker.set('tags', getLocalizedText(tags, 'de').toLowerCase());
        marker.set('title', title);
        marker.set('artist', artist);

        // Set click listener
        marker.addListener('click', () => handleMarkerClick.current(id));

        // Toggle info window on mouseover and mouseout
        marker.addListener('mouseover', () =>
          handleMarkerMouseOver.current(marker)
        );

        marker.addListener('mouseout', () => {
          // Wait some milliseconds to close the info-window to avoid weird toggling effect
          // eslint-disable-next-line max-nested-callbacks
          setTimeout(() => {
            infoWindow?.close();
          }, 200);
        });

        newMarkers.push(marker);
      });
      setMarkers(newMarkers);
    }

    // Clean up markers
    return () => {
      markers.forEach((markerInstance: google.maps.Marker) => {
        markerInstance.setMap(null);
      });
    };
  }, [map, JSON.stringify(workData)]);

  // Filter the markers
  useEffect(() => {
    if (!map) {
      return;
    }

    const newFilteredMarkers = markers.filter(markerInstance => {
      // Filter by category
      const markerCategory = markerInstance.get('category');
      const hasSelectedCategory =
        currentCategoryFilterValues.includes(markerCategory);

      // Filter by year
      const markerYear = markerInstance.get('year');
      const isBeforeYear1945 = checkIsBeforeYear1945(markerYear);
      const filterYearsComparisonValue = isBeforeYear1945
        ? YearFilterValues.before1945
        : YearFilterValues.after1945;
      const hasSelectedYearsSpan = currentYearFilterValues.includes(
        filterYearsComparisonValue
      );

      // Filter by tags
      const markerTags = markerInstance.get('tags');
      const hasSelectedTags =
        currentTagFilterValues.length > 0
          ? currentTagFilterValues?.some((filterValue: string) =>
              markerTags?.includes(filterValue)
            )
          : true;

      return hasSelectedYearsSpan && hasSelectedCategory && hasSelectedTags;
    });

    // Toggle marker's visibility by filtered markers
    markers.forEach(currentMarker => {
      if (newFilteredMarkers.includes(currentMarker)) {
        currentMarker.setVisible(true);
      } else {
        currentMarker.setVisible(false);
      }
    });

    // Dispatch a map marker visibility change event
    document.dispatchEvent(new CustomEvent('map_markers_visibility_changed'));
  }, [
    map,
    JSON.stringify(currentYearFilterValues),
    JSON.stringify(currentCategoryFilterValues),
    JSON.stringify(currentTagFilterValues)
  ]);

  return <MapMarkerClusterer markers={markers} />;
};

export default MapMarkers;
